<template>
    <vx-modal v-model:visible="visible" size="sm" padding="standard" :preventClosing="submitting">
        <template v-slot:header>Remove document</template>
        <template v-slot:subheader>
            {{ props.attachment.file_name ?? props.attachment.uuid }} · {{ props.campaignId }}
        </template>

        <div class="text-sm text-slate-800">
            This cannot be undone; attach a replacement if this was the only opt-in screenshot.
        </div>

        <template v-slot:footer>
            <div class="flex flex-col-reverse gap-4 lg:flex-row">
                <vx-button
                    @click="!submitting && (visible = false)"
                    data-test="cancel-remove"
                    type="button"
                    class="grow lg:w-20 lg:grow-0"
                    size="lg"
                    color="muted"
                >
                    Cancel
                </vx-button>
                <vx-button
                    @click="confirm()"
                    data-test="confirm-remove"
                    :loading="submitting"
                    type="button"
                    class="grow"
                    size="lg"
                    color="danger"
                >
                    Remove
                </vx-button>
            </div>
        </template>
    </vx-modal>
</template>

<script lang="ts" setup>
import { VxButton, VxModal } from '@voxie/frontend-components';
import { ref } from 'vue';
import { useToasted } from '~/composables/useToasted';
import { adminTenDlcRegistrationClient } from '~/services';
import type { TcrSupportingDocument } from '~/services/adminTenDlcRegistrationClient.types';
import { catchRequestError } from '~/utils/request';

const props = defineProps<{ campaignId: string; attachment: TcrSupportingDocument }>();
const emit = defineEmits<{ removed: [] }>();
const visible = defineModel<boolean>('visible', { default: false });

const toasted = useToasted();

const submitting = ref(false);

const confirm = async () => {
    if (submitting.value) {
        return;
    }

    submitting.value = true;

    try {
        await adminTenDlcRegistrationClient.deleteSupportingDocument(props.campaignId, props.attachment.uuid);

        emit('removed');
        visible.value = false;
    } catch (e) {
        toasted.error(catchRequestError(e), { sticky: true });
    } finally {
        submitting.value = false;
    }
};
</script>
